import { Info, Mic, MicOff, Phone, UserRoundX } from 'lucide-react'
import { adjustBrightness } from '../utils/color'

interface SessionControlsProps {
  isSessionActive: boolean
  isConnecting: boolean
  isMuted: boolean
  blockInterruption: boolean
  showInterruptTooltip: boolean
  contactFormTiming: string | undefined
  hasContactInfo: boolean
  primaryColor: string
  botName: string | undefined
  onStartSession: () => void
  onEndSession: () => void
  onToggleMute: () => void
  onToggleInterruption: () => void
  onShowInterruptTooltip: (show: boolean) => void
}

/**
 * Wave 7e: Session control bar — start/active button + mute toggle +
 * interruption toggle (with tooltip) + end-call button.
 *
 * The start button is gated by required contact-info collection
 * (`contactFormTiming !== 'none' && !hasContactInfo`).
 */
export const SessionControls = (props: SessionControlsProps) => {
  const {
    isSessionActive,
    isConnecting,
    isMuted,
    blockInterruption,
    showInterruptTooltip,
    contactFormTiming,
    hasContactInfo,
    primaryColor,
    botName,
    onStartSession,
    onEndSession,
    onToggleMute,
    onToggleInterruption,
    onShowInterruptTooltip,
  } = props

  const requiresContact = (contactFormTiming || 'none') !== 'none' && !hasContactInfo

  return (
    <div style={{ padding: 16, borderBottom: '1px solid #e5e7eb', direction: 'ltr' }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={onStartSession}
          disabled={isSessionActive || isConnecting || requiresContact}
          style={{
            flex: 1,
            padding: '12px 16px',
            borderRadius: 8,
            border: 'none',
            background: isSessionActive
              ? '#166534'
              : (requiresContact
                ? '#e5e7eb'
                : `linear-gradient(135deg, ${primaryColor}, ${adjustBrightness(primaryColor, 0.8)})`),
            color: requiresContact ? '#9ca3af' : 'white',
            cursor: isSessionActive || requiresContact ? 'default' : 'pointer',
            fontWeight: isSessionActive ? 700 : 500,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            opacity: (isConnecting || requiresContact) ? 0.7 : 1,
            textShadow: isSessionActive ? '0 1px 1px rgba(0, 0, 0, 0.35)' : 'none',
          }}
        >
          <Mic size={18} />
          {isConnecting ? 'מתחבר...' : isSessionActive ? 'פעיל' : 'התחל שיחה'}
        </button>

        {/* Interruption Toggle Button */}
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <button
            type="button"
            onClick={onToggleInterruption}
            disabled={!isSessionActive}
            onMouseEnter={() => onShowInterruptTooltip(true)}
            onMouseLeave={() => onShowInterruptTooltip(false)}
            style={{
              width: 48,
              height: 48,
              padding: 0,
              borderRadius: 8,
              border: 'none',
              background: blockInterruption ? '#6b7280' : '#22c55e',
              color: 'white',
              cursor: isSessionActive ? 'pointer' : 'default',
              opacity: isSessionActive ? 1 : 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
            }}
          >
            <UserRoundX size={20} />
            <Info
              size={10}
              style={{
                position: 'absolute',
                top: 4,
                right: 4,
                opacity: 0.8,
              }}
            />
          </button>

          {/* Tooltip */}
          {showInterruptTooltip && (
            <div
              style={{
                position: 'absolute',
                bottom: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                marginBottom: 8,
                padding: '8px 12px',
                background: 'rgba(0, 0, 0, 0.9)',
                color: 'white',
                borderRadius: 6,
                fontSize: 12,
                whiteSpace: 'nowrap',
                zIndex: 10000,
                pointerEvents: 'none',
                maxWidth: 'max-content',
                textAlign: 'center',
                direction: 'rtl',
              }}
            >
              {blockInterruption
                ? `${botName || 'הסוכנת'} תסיים לדבר לפני שתוכלו`
                : `ניתן לקטוע את ${botName || 'הסוכנת'}`}
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 0,
                  height: 0,
                  borderLeft: '6px solid transparent',
                  borderRight: '6px solid transparent',
                  borderTop: '6px solid rgba(0, 0, 0, 0.9)',
                }}
              />
            </div>
          )}
        </div>

        {/* Mute toggle (middle of traffic-light trio: red end / yellow mute / green interrupt) */}
        <button
          type="button"
          onClick={onToggleMute}
          disabled={!isSessionActive}
          style={{
            width: 48,
            height: 48,
            padding: 0,
            borderRadius: 8,
            border: 'none',
            background: '#f59e0b',
            color: 'white',
            cursor: isSessionActive ? 'pointer' : 'default',
            opacity: isSessionActive ? 1 : 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isMuted ? <MicOff size={20} /> : <Mic size={20} />}
        </button>

        <button
          type="button"
          onClick={onEndSession}
          disabled={!isSessionActive}
          style={{
            width: 48,
            height: 48,
            padding: 0,
            borderRadius: 8,
            border: 'none',
            background: '#ef4444',
            color: 'white',
            cursor: isSessionActive ? 'pointer' : 'default',
            opacity: isSessionActive ? 1 : 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Phone size={20} />
        </button>
      </div>
    </div>
  )
}
